import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import EventsListingCard from './EventsListingCard'
import EventsListingCardSkeleton from './EventsListingCardSkeleton'
import FilterBar from '../My--Bidding--design/FilterBar'
import BidFilterCarousel from '../My--Bidding--design/comp/BidFilterCarousel'
import { HiOutlineArrowLeft } from "react-icons/hi"
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { API_BASE_URL } from '../utils'
import { useGeoLocation } from '../context/LocationContext'
import axios from 'axios'
import './EvenetListingPage.css'
import '../My--Bidding--design/PartyPackageListingPage.css'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import EventFilterCarousel from './comp/EventFilterCarousel'
import EventNewCard from './comp/EventNewCard'
import BidNewCardSkeleton from '../My--Bidding--design/comp/BidNewCardSkeleton'


const url = `${API_BASE_URL}/master/assets/uploads/`;

const filters = [
    {
        "label": "Today",
        "type": "filter",
        "icon": "CiCalendarDate"
    },
    {
        "label": "Tomorrow",
        "type": "filter",
        "icon": "CiCalendarDate"
    },
    {
        "label": "Carnival",
        "type": "filter",
        "icon": "FaStar"
    },
    {
        "label": "Distance",
        "type": "dropdown",
        "icon": "IoIosArrowDown",
        "options": ["Under 5 Km", "Under 10 Km", "Under 20 Km", "Under 30 Km", "Under 50 Km"]
    },
]


export default function EvenetListingPage() {

    const { latitude, longitude } = useGeoLocation();
    const navigate = useNavigate();
    const loaderRef = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();

    // Single source of truth for filters
    const [filtersAndSort, setFiltersAndSort] = useState({
        selectedDropdowns: {},
        selectedPills: [],
    });

    const [typeOfCarnival, setTypeOfCarnival] = useState('all');
    const [showFilter, setShowFilter] = useState(false);

    const today = new Date().toISOString().split('T')[0];
    const path = window.location.pathname;
    const bannerheading = path === '/events' ? 'Events' : "Today's Events";

    function generateClubUrl(clubName, cityName, areaName, title) {
        const format = (str) => (str ? str.trim().replace(/\s+/g, "-").toLowerCase() : "");
        return `/event/${format(clubName)}-${format(cityName)}-${format(areaName)}/${format(title)}`;
    }

    // ✅ Load filters from URL ONCE on mount
    useEffect(() => {
        const review = searchParams.get("review");
        const distance = searchParams.get("distance");
        const sort = searchParams.get("sort");
        const pillsParam = searchParams.get("filter");

        const pills = pillsParam ? pillsParam.split(",") : [];
        const hasFilters = review || distance || sort || pills.length > 0;

        if (hasFilters) {
            setFiltersAndSort({
                selectedDropdowns: {
                    ...(review && { Review: [review] }),
                    ...(distance && { Distance: distance }),
                    ...(sort && { "Sort By": sort }),
                },
                selectedPills: pills,
            });
        }
    }, []); // Only run once on mount

    // ✅ Update URL when filters change (debounced to avoid excessive updates)
    useEffect(() => {
        const params = new URLSearchParams();

        const review = filtersAndSort.selectedDropdowns?.Review?.[0];
        const distance = filtersAndSort.selectedDropdowns?.Distance;
        const sort = filtersAndSort.selectedDropdowns?.["Sort By"];
        const pills = filtersAndSort.selectedPills;

        if (review) params.set("review", review);
        if (distance) params.set("distance", Array.isArray(distance) ? distance[0] : distance);
        if (sort) params.set("sort", sort);
        if (pills?.length) params.set("filter", pills.join(","));

        // Only update if params actually changed
        const currentParams = searchParams.toString();
        const newParams = params.toString();

        if (currentParams !== newParams) {
            setSearchParams(params, { replace: true });
        }
    }, [filtersAndSort]);

    // Fetch Events
    const fetchEvents = async ({ pageParam = 1 }) => {
        const formData = new FormData();
        formData.append('latitude', latitude);
        formData.append('longitude', longitude);
        formData.append('limit', 10);
        formData.append('page', pageParam);

        const response = await axios.post(
            `${API_BASE_URL}/master/APIs/ClubEvent/allEvents`,
            formData
        );

        if (response.data.status) {
            return {
                events: response.data.data,
                nextPage: response.data.total > pageParam * 10 ? pageParam + 1 : null
            };
        } else {
            return {
                events: [],
                nextPage: null
            };
        }
    };

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading
    } = useInfiniteQuery({
        queryKey: ['events', latitude, longitude],
        queryFn: fetchEvents,
        getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
        enabled: !!latitude && !!longitude,
        keepPreviousData: true,
        staleTime: Infinity,
        cacheTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    });

    const eventsData = useMemo(() =>
        data ? data.pages.flatMap(page => page.events) : [],
        [data]
    );

    // Fetch Carnival Passes
    const {
        data: passes = [],
        isLoading: carnivalPassesLoading,
        isError: carnivalPassesIsError,
        error: carnivalPassesError
    } = useQuery({
        queryKey: ['carnivals', typeOfCarnival],
        queryFn: async () => {
            const response = await axios.get(
                `${API_BASE_URL}/master/Api/getData/allCarnivalPassOfType?type=${typeOfCarnival}`
            );
            return response.data.status ? response.data.data : [];
        },
        enabled: Boolean(typeOfCarnival),
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    // ✅ Optimized filter logic with useMemo
    const filteredData = useMemo(() => {
        const { selectedDropdowns, selectedPills } = filtersAndSort;

        // If Carnival is selected, return carnival passes
        if (selectedPills?.includes("Carnival")) {
            return passes;
        }

        // Check if any filter is applied
        const hasReview = selectedDropdowns?.Review?.length > 0;
        const hasDistance = selectedDropdowns?.Distance;
        const hasSort = selectedDropdowns?.["Sort By"];
        const hasToday = selectedPills?.includes("Today");
        const hasTomorrow = selectedPills?.includes("Tomorrow");

        const hasAnyFilter = hasReview || hasDistance || hasSort || hasToday || hasTomorrow;

        // If no filters, return empty array (show all events)
        if (!hasAnyFilter) {
            return [];
        }

        // Apply filters
        let filtered = eventsData.filter((item) => {
            // Review Filter
            if (hasReview) {
                const selectedReview = parseInt(selectedDropdowns.Review[0]);
                const itemRating = parseFloat(item.totalAverageRating);
                const lowerBound = selectedReview - 0.4;
                const upperBound = selectedReview + 0.5;

                if (isNaN(itemRating) || itemRating < lowerBound || itemRating >= upperBound) {
                    return false;
                }
            }

            // Distance Filter
            if (hasDistance) {
                const distanceValue = Array.isArray(selectedDropdowns.Distance)
                    ? selectedDropdowns.Distance[0]
                    : selectedDropdowns.Distance;
                const selectedDistance = parseInt(distanceValue.match(/\d+/)?.[0] || "0", 10);
                const itemDistance = parseFloat(item.distance);

                if (isNaN(itemDistance) || itemDistance > selectedDistance) {
                    return false;
                }
            }

            // Today Filter
            if (hasToday) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const eventDate = new Date(item.event_date);
                eventDate.setHours(0, 0, 0, 0);

                if (eventDate.getTime() !== today.getTime()) {
                    return false;
                }
            }

            // Tomorrow Filter
            if (hasTomorrow) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const tomorrow = new Date(today);
                tomorrow.setDate(today.getDate() + 1);

                const eventDate = new Date(item.event_date);
                eventDate.setHours(0, 0, 0, 0);

                if (eventDate.getTime() !== tomorrow.getTime()) {
                    return false;
                }
            }

            return true;
        });

        // Apply sorting
        if (hasSort) {
            const sortType = selectedDropdowns["Sort By"];
            filtered = [...filtered].sort((a, b) => {
                if (sortType === "A to Z") return a.title.localeCompare(b.title);
                if (sortType === "Z to A") return b.title.localeCompare(a.title);
                return 0;
            });
        }

        return filtered;
    }, [filtersAndSort, eventsData, passes]);



    // ✅ Filter-wise result counts
    const filterWiseCount = useMemo(() => {
        const counts = {};
        const events = eventsData || [];

        // 1️⃣ Distance counts
        const distanceOptions = [
            "Under 5 Km",
            "Under 10 Km",
            "Under 20 Km",
            "Under 30 Km",
            "Under 50 Km",
        ];

        distanceOptions.forEach((opt) => {
            const selectedDistance = parseInt(opt.match(/\d+/)?.[0] || "0", 10);
            const matched = events.filter(
                (item) => parseFloat(item.distance) <= selectedDistance
            );
            counts[opt] = matched.length;
        });

        // 2️⃣ Today & Tomorrow pills
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        counts["Today"] = events.filter((item) => {
            const eventDate = new Date(item.event_date);
            eventDate.setHours(0, 0, 0, 0);
            return eventDate.getTime() === today.getTime();
        }).length;

        counts["Tomorrow"] = events.filter((item) => {
            const eventDate = new Date(item.event_date);
            eventDate.setHours(0, 0, 0, 0);
            return eventDate.getTime() === tomorrow.getTime();
        }).length;

        // 3️⃣ Carnival count (from passes API)
        counts["Carnival"] = passes?.length || 0;

        return counts;
    }, [eventsData, passes]);



    // ✅ Merge events and carnival passes
    const mergedEvents = useMemo(() => {
        const interval = 4;
        const merged = [];
        let carnivalIndex = 0;

        eventsData.forEach((event, idx) => {
            merged.push(event);

            if ((idx + 1) % interval === 0 && carnivalIndex < passes.length) {
                merged.push(passes[carnivalIndex]);
                carnivalIndex++;
            }
        });

        // Append remaining passes
        while (carnivalIndex < passes.length) {
            merged.push(passes[carnivalIndex]);
            carnivalIndex++;
        }

        return merged;
    }, [eventsData, passes]);

    // Infinite scroll observer
    const handleObserver = useCallback((entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    useEffect(() => {
        const observer = new IntersectionObserver(handleObserver, { threshold: 1 });
        if (loaderRef.current) observer.observe(loaderRef.current);
        return () => {
            if (loaderRef.current) observer.unobserve(loaderRef.current);
        };
    }, [handleObserver]);

    return (
        <>
            <Helmet>
                <title>Book Events & Celebrations in Delhi NCR – Top Venues & Deals</title>
                <meta name="description" content="Looking for event booking in Delhi NCR? Discover top venues, curated packages, and best deals for birthdays, weddings, and corporate events." />
            </Helmet>

            <div className='eventlist-container'>
                <div className="eventlist-hero-section">
                    <h4>
                        <button onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/"))} className='phc-back-button'>
                            <HiOutlineArrowLeft />
                        </button>
                        Events
                    </h4>
                </div>

                <div className='pppftb-filter'>
                    <FilterBar
                        filterList={filters}
                        filtersAndSort={filtersAndSort}
                        setFiltersAndSort={setFiltersAndSort}
                        setShowFilter={setShowFilter}
                        filterWiseCount={filterWiseCount}
                    />
                </div>

                {filteredData.length > 0 && (
                    <div className='bid-listing-page-new-header-crousale'>
                        <EventFilterCarousel data={filteredData} />
                    </div>
                )}

                <div className="event-cards-page-container">
                    {isLoading
                        ? Array.from({ length: 6 }).map((_, index) => <BidNewCardSkeleton key={index} />)
                        : mergedEvents.map((event, index) => (
                            <div key={`${event.id || event.title}-${index}`} style={{ width: "100%" }}>
                                <EventNewCard data={event} />
                            </div>
                        ))
                    }

                    {isFetchingNextPage &&
                        Array.from({ length: 6 }).map((_, index) => <BidNewCardSkeleton key={`loading-${index}`} />)
                    }

                    <div ref={loaderRef} style={{ height: "1px" }} />
                </div>

                <div className='event-page-design-bottom-footer-tags-section'>
                    <div className='event-page-detail-page-design-bottom-footer-tags'>
                        <div className='event-page-detail-page-design-bottom-footer-tags-text'>
                            <h1>Book Top Venues for Events, Birthdays & Celebrations in Delhi NCR</h1>
                            <p>Plan unforgettable events with ease on PartyWitty! Whether it's a birthday, engagement, farewell, or corporate gathering, find the top-rated venues in Delhi, Noida, and Gurgaon. From luxurious lounges to rooftop cafés and private party halls, discover the perfect spot that fits your celebration style. Explore verified listings, customize food and drink packages, and book instantly with exclusive group offers. Celebrate every moment at the best event venues across Delhi NCR — only on PartyWitty.</p>
                        </div>
                        <div className='event-page-detail-page-design-bottom-footer-tags-text'>
                            <h1>Host Memorable Celebrations with Event Packages & Table Bookings</h1>
                            <p>Make every event special with PartyWitty's exclusive packages and instant table bookings. Whether it's a birthday bash, office party, or anniversary celebration, find premium clubs, lounges, and restaurants across Delhi NCR. Compare event packages, check real-time availability, and enjoy up to 50% off on bookings. From private sections to dance floors and BYOB venues — plan your perfect celebration in just a few clicks. Party planning made smooth, stylish, and affordable.</p>
                        </div>
                        <div className='event-page-detail-page-design-bottom-footer-tags-text'>
                            <h1>Explore Celebration Packages for Events & Parties Across Delhi NCR</h1>
                            <p>Celebrate in style with PartyWitty's curated event and celebration packages! Discover top venues in Delhi, Noida, and Gurgaon for birthdays, kitty parties, corporate events, and more. Choose from themed lounges, rooftop spots, and private dining areas — all offering customizable food, drinks, and music. With verified listings, real-time booking, and exciting deals, your party planning becomes stress-free and budget-friendly. Book now and let the celebrations begin!</p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}